import * as React from 'react';

function getLocalStorageItem<T>({ key, initialValue }: { key: string; initialValue: T }): T {
	try {
		const item = window.localStorage.getItem(key);
		if (item) {
			return JSON.parse(item);
		}
	} catch (error) {
		console.warn(`Failed to read localStorage key "${key}":`, error);
	}
	return initialValue;
}

function setLocalStorageItem<T>(key: string, value: T): void {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.warn(`Failed to write localStorage key "${key}":`, error);
	}
}

type SetValueFunction<T> = (value: T | ((prevValue: T) => T)) => void;

/**
 * @deprecated useLocalStorageState instead which is reactive and works across tabs
 */
export function useLocalStorage<T>({
	key,
	initialValue,
}: {
	key: string;
	initialValue?: T;
}): [T | undefined, SetValueFunction<T | undefined>] {
	const [storedValue, setStoredValue] = React.useState(() => getLocalStorageItem<T>({ key, initialValue }));
	const [prevKey, setPrevKey] = React.useState(key);
	if (key !== prevKey) {
		setPrevKey(key);
		setStoredValue(() => getLocalStorageItem<T>({ key, initialValue }));
	}
	const setValue = React.useCallback(
		(value: T | ((prevValue: T) => T)) => {
			setStoredValue(prev => {
				const newValue = typeof value === 'function' ? (value as (prevValue: T) => T)(prev) : value;
				setLocalStorageItem(key, newValue);
				return newValue;
			});
		},
		[key]
	);
	return [storedValue, setValue];
}

// Module-level subscriber registry
const listeners = new Map<string, Set<() => void>>();

function subscribe(key: string, callback: () => void) {
	if (!listeners.has(key)) {
		listeners.set(key, new Set());
	}
	listeners.get(key)!.add(callback);
	return () => {
		const subs = listeners.get(key)!;
		subs.delete(callback);
		if (subs.size === 0) listeners.delete(key);
	};
}

function notifyListeners(key: string) {
	listeners.get(key)?.forEach(cb => cb());
}

// Cross-tab sync — fires when *another* tab modifies localStorage
window.addEventListener('storage', e => {
	if (e.key) notifyListeners(e.key);
});

export function useLocalStorageState<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
	// getSnapshot returns raw string — Object.is comparison works naturally
	const snapshot = React.useSyncExternalStore(
		cb => subscribe(key, cb),
		() => localStorage.getItem(key),
		() => JSON.stringify(initialValue) // SSR fallback
	);

	const value: T = snapshot !== null ? JSON.parse(snapshot) : initialValue;

	const setValue = React.useCallback(
		(next: T | ((prev: T) => T)) => {
			const current = localStorage.getItem(key);
			const parsed: T = current !== null ? JSON.parse(current) : initialValue;
			const resolved = typeof next === 'function' ? (next as (prev: T) => T)(parsed) : next;
			localStorage.setItem(key, JSON.stringify(resolved));
			notifyListeners(key); // triggers re-render in all subscribers
		},
		[key, initialValue]
	);

	return [value, setValue];
}
